import { useGet4 } from "@synthesis/shared-services/internal/payment/hooks";
import { paymentViewInvoiceStatusEnum } from "@synthesis/shared-services/internal/payment/types";
import { IconClose } from "@synthesis/shared-icons";
import { Button, Tag, Tooltip } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";

import { isEditableExternalApplicationStatus } from "../../../constants";
import { useClientType } from "../../../shared/hooks/use-client-type";
import { useUserRoles } from "../../../shared/hooks/use-user-roles";
import { canManageRegionDepartments } from "./application-detail-controls.lib";
import { ApplicationDeadlineControlBadge } from "../../../components/application/application-deadline-control-badge";
import { ApplicationStatusBadge } from "../../../components/application/application-status";
import {
  FINAL_DECISION_APPROVER_OUTCOME_ICON,
  FINAL_DECISION_APPROVER_OUTCOME_LABEL,
  FINAL_DECISION_APPROVER_OUTCOME_TO_UI_STATUS,
} from "../../../components/application-status-ui";
import { getDictionaryOptionLabel } from "../../create-application-page/create-application-dictionaries.lib";
import { useApplicationDictionariesContext } from "../application-dictionaries-context";
import {
  getPaymentTermsDisplayByPaymentFlag,
  type ApplicationDetail,
  type ApplicationDetailApproverApprovalOutcome,
} from "../application-detail-page.lib";
import { useApplicationDetail } from "../hooks/use-application-detail";
import { ApplicationControls } from "./application-controls";
import { ApplicationInitiatorField } from "./application-initiator-field";
import { DeleteRegionDepartmentModal } from "./application-control-delete-region-department-modal";
import { fetchRegionalDepartments, regionalDepartmentsQueryKey, type RegionDepartment } from "./application-control-region-departments-modal.lib";

const INVOICE_ISSUED_BADGE_LABEL = "Выставлен счёт";

type ApplicationDetailHeaderProps = {
  className?: string;
};

const MetadataField = ({ label, value, className }: { label: string; value: ReactNode; className?: string }) => (
  <div className={cn("flex min-w-0 flex-col", className)}>
    <p className="subtitle-sm text-type-light h-5 truncate">{label}</p>
    <div className="paragraph-base text-type-common py-2">{value}</div>
  </div>
);

const MetadataSection = ({ title, children, className }: { title: string; children: ReactNode; className?: string }) => (
  <section className={cn("flex w-full flex-col gap-4", className)}>
    <h2 className="subtitle-lg text-type-common">{title}</h2>
    {children}
  </section>
);

const ApproverNameWithOutcome = ({ name, outcome }: { name: string; outcome: ApplicationDetailApproverApprovalOutcome | null }) => {
  if (!outcome) {
    return <span className="min-w-0">{name}</span>;
  }

  const OutcomeIcon = FINAL_DECISION_APPROVER_OUTCOME_ICON[outcome];

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <span className="min-w-0">{name}</span>
      <Tag
        size="sm"
        status={FINAL_DECISION_APPROVER_OUTCOME_TO_UI_STATUS[outcome]}
        className="w-fit shrink-0"
        leftIcon={<OutcomeIcon aria-hidden className="size-5 shrink-0" />}
      >
        {FINAL_DECISION_APPROVER_OUTCOME_LABEL[outcome]}
      </Tag>
    </div>
  );
};

const RegionDepartmentChip = ({
  department,
  canManageDepartments,
  onDelete,
}: {
  department: RegionDepartment;
  canManageDepartments: boolean;
  onDelete: (department: RegionDepartment) => void;
}) => {
  const nameRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const element = nameRef.current;

    if (element) {
      setIsTruncated(element.scrollWidth > element.clientWidth);
    }
  }, []);

  useLayoutEffect(() => {
    const element = nameRef.current;

    if (!element) {
      return undefined;
    }

    checkTruncation();

    const frame = requestAnimationFrame(checkTruncation);
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(element);

    if (element.parentElement) {
      observer.observe(element.parentElement);
    }

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [department.name, checkTruncation]);

  return (
    <li className="bg-neutrals-95 group flex w-fit min-w-[80px] max-w-[240px] items-center gap-1.5 rounded-[14px] py-1.5 pl-3 pr-1">
      <Tooltip description={department.name} disabled={!isTruncated} arrowSide="top" arrowAlign="center">
        <span ref={nameRef} tabIndex={isTruncated ? 0 : undefined} className="subtitle-xs text-type-minor block min-w-0 flex-1 truncate">
          {department.name}
        </span>
      </Tooltip>
      {canManageDepartments ? (
        <button
          type="button"
          aria-label={`Удалить ${department.name}`}
          onClick={() => onDelete(department)}
          className="text-type-light hover:bg-neutrals-90 hover:text-type-common flex size-5 shrink-0 items-center justify-center rounded-full opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
        >
          <IconClose className="size-4" aria-hidden />
        </button>
      ) : null}
    </li>
  );
};

const SecretaryApproverGroupsSection = ({ application }: { application: ApplicationDetail }) => {
  const secretaryInfo = application.secretaryInfo;
  const clientType = useClientType();
  const roles = useUserRoles();
  const canManageDepartments = canManageRegionDepartments(application, clientType, roles);

  const { data: regionalDepartments = [] } = useQuery({
    queryKey: regionalDepartmentsQueryKey(application.requestId),
    queryFn: ({ signal }) => fetchRegionalDepartments(application.requestId, signal),
    enabled: Boolean(secretaryInfo),
  });

  const [departmentToDelete, setDepartmentToDelete] = useState<RegionDepartment | null>(null);

  if (!secretaryInfo) {
    return null;
  }

  const { approverGroups } = secretaryInfo;

  return (
    <div className="flex w-full gap-1">
      <MetadataSection title="Согласующие" className="min-w-0 max-w-[33.125rem] flex-1">
        <div className="flex flex-col gap-1">
          <MetadataField
            label="НТП"
            value={<ApproverNameWithOutcome name={approverGroups.ntpChief} outcome={approverGroups.ntpChiefApprovalOutcome} />}
          />
          <MetadataField
            label="Аппарат НГ"
            value={<ApproverNameWithOutcome name={approverGroups.ngOffice} outcome={approverGroups.ngOfficeApprovalOutcome} />}
          />
        </div>
      </MetadataSection>

      <MetadataSection title="Причастные" className="min-w-0 flex-1">
        <MetadataField
          label="Технический отдел региона и причастные подразделения"
          value={
            regionalDepartments.length > 0 ? (
              <ul className="flex flex-wrap gap-2">
                {regionalDepartments.map((department) => (
                  <RegionDepartmentChip
                    key={department.id}
                    department={department}
                    canManageDepartments={canManageDepartments}
                    onDelete={setDepartmentToDelete}
                  />
                ))}
              </ul>
            ) : (
              "—"
            )
          }
        />
      </MetadataSection>

      {departmentToDelete ? (
        <DeleteRegionDepartmentModal
          requestId={application.requestId}
          department={departmentToDelete}
          departments={regionalDepartments}
          onClose={() => setDepartmentToDelete(null)}
        />
      ) : null}
    </div>
  );
};

const SecretaryDeadlineControlSection = ({ application }: { application: ApplicationDetail }) => {
  const deadlineControl = application.secretaryInfo?.deadlineControl;

  if (!deadlineControl) {
    return null;
  }

  return (
    <MetadataSection title="Контроль срока рассмотрения">
      <div className="grid gap-x-1 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetadataField label="Дата поступления" value={deadlineControl.receivedAt} className="w-[10.875rem]" />
        <MetadataField label="Рассмотреть до" value={deadlineControl.reviewDueAt} className="w-[10.875rem]" />
        <MetadataField
          label="Осталось"
          value={
            deadlineControl.remaining ? (
              <ApplicationDeadlineControlBadge state={deadlineControl.remaining.state} label={deadlineControl.remaining.label} />
            ) : (
              "—"
            )
          }
          className="w-[10.875rem]"
        />
        <MetadataField label="Ответственный" value={deadlineControl.responsible} className="w-[10.875rem]" />
      </div>
    </MetadataSection>
  );
};

const GeneralInfoSection = ({ application, withTitle }: { application: ApplicationDetail; withTitle?: boolean }) => {
  const { dictionaries } = useApplicationDictionariesContext();
  const clientType = useClientType();
  const isInternal = clientType === "internal";
  // Подписка только на кэш данных оплаты (без запроса): значение приходит из вкладки «Оплата»
  const { data: paymentView } = useGet4({ path: { id: isInternal ? (application.requestId ?? "") : "" } }, { query: { enabled: false } });
  const railwayLabel = getDictionaryOptionLabel(dictionaries.railways, application.railway);
  const paymentTerms = (isInternal ? getPaymentTermsDisplayByPaymentFlag(paymentView?.paymentFlag) : null) ?? application.paymentTerms;

  const content = (
    <div className="grid gap-x-1 gap-y-4 sm:grid-cols-2 lg:grid-cols-[10.875rem_10.875rem_10.875rem_10.875rem_minmax(0,1fr)_minmax(0,1fr)]">
      <MetadataField label="Тип заявки" value={application.categoryLabel} />
      <MetadataField label="Дата создания" value={application.createdAt} />
      <MetadataField label="Дата изменения" value={application.updatedAt} />
      <MetadataField label="Условия оплаты" value={paymentTerms} />
      <MetadataField label="Железная дорога" value={railwayLabel} />
      <MetadataField
        label="Инициатор"
        value={<ApplicationInitiatorField initiatorEmail={application.initiatorEmail} initiatorName={application.initiator} />}
      />
    </div>
  );

  if (withTitle) {
    return <MetadataSection title="Общая информация по заявке">{content}</MetadataSection>;
  }

  return content;
};

export const ApplicationDetailHeader = ({ className }: ApplicationDetailHeaderProps) => {
  const { application } = useApplicationDetail();
  const navigate = useNavigate();
  const clientType = useClientType();
  const roles = useUserRoles();
  const isInternal = clientType === "internal";
  const { data: paymentView } = useGet4(
    { path: { id: isInternal ? (application?.requestId ?? "") : "" } },
    { query: { enabled: isInternal && Boolean(application?.requestId) } },
  );

  if (!application) {
    return null;
  }
  const canEditApplication = clientType === "external" && roles.isExternalApplicant && isEditableExternalApplicationStatus(application.status);
  const showInvoiceIssuedBadge = isInternal && paymentView?.invoiceStatus === paymentViewInvoiceStatusEnum.SENT && !paymentView.proofFileId;

  return (
    <section className={cn("bg-neutrals-white flex flex-col gap-6 rounded-xl p-6", className)}>
      <h1 className="text-type-common heading-h4 text-[32px] leading-10">
        <span className="text-type-light">Заявка {application.id} </span>
        {application.title}
      </h1>

      <div className="flex w-full flex-wrap items-center gap-4">
        <div className="flex flex-wrap items-center gap-3">
          {application.status ? (
            <ApplicationStatusBadge contour={clientType ?? "internal"} label={application.statusLabel} status={application.status} />
          ) : (
            <span className="text-type-minor">—</span>
          )}
          {showInvoiceIssuedBadge ? (
            <Tag size="status" status="TO_REUPLOAD">
              {INVOICE_ISSUED_BADGE_LABEL}
            </Tag>
          ) : null}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          {canEditApplication && (
            <Button
              type="button"
              theme="secondary"
              size="md"
              onClick={() => navigate({ to: "/applications/create", search: { draftId: application.requestId } })}
            >
              {application.status === "REVISION_REQUIRED" ? "Доработать заявку" : "Редактировать черновик"}
            </Button>
          )}
          <ApplicationControls application={application} />
        </div>
      </div>

      {isInternal ? (
        <>
          <SecretaryApproverGroupsSection application={application} />
          <SecretaryDeadlineControlSection application={application} />
          <GeneralInfoSection application={application} withTitle />
        </>
      ) : (
        <GeneralInfoSection application={application} />
      )}
    </section>
  );
};
